import { useEffect, useState } from 'react';

import { CUSTOM_EVENTS_NAMES } from '@/constant/events';
import { UpdateAnchorFn } from '@/types/navigation';
import { equals } from '@/utils/common';
import { scrollToAnchor } from '@/utils/navigation';
import { skipHashTag } from '@/utils/strings';

type SuccessHandler = (time: number) => void;

export const useAnchor = (onUpdate?: SuccessHandler) => {
  const [anchor, setAnchor] = useState<string | null>(null);

  const outerUpdatedAnchor: UpdateAnchorFn = (hash, element, onUpdate) => {
    element?.scrollIntoView?.({ behavior: 'smooth', block: 'center', inline: 'center' });
    window.history.replaceState(null, '', `#${hash}`);
    onUpdate?.();
  };

  useEffect(() => {
    const updateAnchor = (eventData?: Event) => {
      const hash = window.location.hash;
      const anchorWithoutHash = skipHashTag(hash);

      if (Boolean(anchorWithoutHash)) {
        setAnchor(anchorWithoutHash);

        if (equals(eventData?.detail?.initiator, 'click')) {
          return onUpdate?.(Date.now());
        }
        return;
      }
      setAnchor('');
    };
    updateAnchor();
    window.addEventListener(CUSTOM_EVENTS_NAMES.historyReplaceState, updateAnchor);

    return () => {
      window.removeEventListener(CUSTOM_EVENTS_NAMES.historyReplaceState, updateAnchor);
    };
  }, []);

  return { anchor, updateAnchor: outerUpdatedAnchor };
};

export const useScrollToAnchorOnMount = (onScrollToElement: SuccessHandler) => {
  useEffect(() => {
    const anchor = window.location.hash;
    if (anchor) {
      const anchorWithoutHash = anchor.startsWith('#') ? anchor.substring(1) : anchor;

      scrollToAnchor(anchorWithoutHash, onScrollToElement);

      return;
    }
  }, []);
};
